import { useApiClient, useFetchInfiniteES } from '@marageti/z4-lib';
import {
  SearchResult,
  FilterConstraint,
} from '@marageti/z4-sdk/lib/entity';
import { Review, ReviewStatus } from '@marageti/z4-sdk/lib/travel';
import { useMemo } from 'react';
import {
  REVIEW_PUBLICATION_STATUS,
  buildReviewPublicationStatusFilters,
} from '../components/infinite-reviews-list/utils';

interface PossibleSearchFields {
  fullText?: string;
  rating?: string[];
  order?: string;
  status?: string[];
}

const useReviewSearch = ({
  fullText = '',
  rating = [],
  order = 'asc',
  status = [],
}: PossibleSearchFields) => {
  const apiClient = useApiClient();

  const getSearchRequest = () => {
    const filterBy: FilterConstraint[] = [];
    const doFullTextSearch = fullText.length > 2;
    const sortBy = doFullTextSearch ? {} : {
      sortBy: [{
        field: 'createdAt',
        order,
      }],
    };

    if (rating.length) {
      filterBy.push({ field: 'rating', value: rating });
    }

    filterBy.push(...buildReviewPublicationStatusFilters(status));

    return {
      filterBy,
      fullText: doFullTextSearch ? fullText : undefined,
      phrase: fullText.includes('@'),
      pagination: { limit: 30, page: 1 },
      ...sortBy,
    };
  };

  const searchRequest = getSearchRequest();

  const {
    data,
    error,
    hasMore,
    isLoading,
    loadMore,
  } = useFetchInfiniteES<Review>(apiClient.reviewsClient.postReviewsSearch, searchRequest, { revalidateOnFocus: false });

  const reviews = useMemo(() => {
    const hits = data?.flatMap((searchResult: SearchResult<Review>) => searchResult.hits);
    const draft = status.includes(REVIEW_PUBLICATION_STATUS.DRAFT);
    const published = status.includes(REVIEW_PUBLICATION_STATUS.PUBLISHED);
    const unpublished = status.includes(REVIEW_PUBLICATION_STATUS.UNPUBLISHED);

    if (draft && published && !unpublished) {
      return hits?.filter((review) => (
        review.status === ReviewStatus.ReviewStatusPending
        || (review.status === ReviewStatus.ReviewStatusCompleted && review.published)
      ));
    }

    return hits;
  }, [data, status]);

  return {
    reviews,
    error,
    hasMore,
    isLoading,
    loadMore,
    totalHits: data?.[0]?.totalHits || 0,
  };
};

export default useReviewSearch;
